const WS_URL = import.meta.env.VITE_WS_URL;

export class WebSocketService {
    private static instance: WebSocketService;
    private ws: WebSocket | null = null;
    private reconnectInterval = 5000; // 5 seconds
    private maxReconnectAttempts = 5;
    private reconnectAttempts = 0;
    private isConnected = false;
    private shouldReconnect = true;
    private reconnectTimeoutId?: NodeJS.Timeout;
    private eventListeners: ((data: unknown) => void)[] = [];
    private connectionListeners: ((status: boolean) => void)[] = [];

    private constructor() {
        this.connect();
    }

    public static getInstance(): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }

        return WebSocketService.instance;
    }

    private connect() {
        if (this.ws) {
            this.ws.close();
        }

        // Stop reconnecting after max attempts
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.warn('Max WebSocket reconnect attempts reached. Stopping reconnection.');
            return;
        }

        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
            this.isConnected = true;
            this.reconnectAttempts = 0; // Reset on successful connection
            this.notifyConnectionStatus(true);
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.eventListeners.forEach((callback) => callback(data));
            }
            catch (error) {
                console.error("Failed to parse WebSocket message:", error);
            }
        };

        this.ws.onerror = (error) => {
            console.error("🚨 WebSocket error:", error);
            this.isConnected = false;
            this.notifyConnectionStatus(false);
        };

        this.ws.onclose = () => {
            this.isConnected = false;
            this.notifyConnectionStatus(false);
            
            // Only reconnect if we should and haven't hit max attempts
            if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                console.log(`WebSocket reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
                
                // Clear any existing timeout
                if (this.reconnectTimeoutId) {
                    clearTimeout(this.reconnectTimeoutId);
                }
                
                this.reconnectTimeoutId = setTimeout(() => this.connect(), this.reconnectInterval);
            }
        };
    }

    public disconnect() {
        this.shouldReconnect = false;
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }

    public send(data: unknown) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        }
    }

    public subscribe(callback: (data: unknown) => void) {
        this.eventListeners.push(callback);
    }

    public unsubscribe(callback: (data: unknown) => void) {
        this.eventListeners = this.eventListeners.filter((cb) => cb !== callback);
    }

    public subscribeConnectionStatus(callback: (status: boolean) => void) {
        this.connectionListeners.push(callback);
        callback(this.isConnected);
    }

    public unsubscribeConnectionStatus(callback: (status: boolean) => void) {
        this.connectionListeners = this.connectionListeners.filter((cb) => cb !== callback);
    }

    private notifyConnectionStatus(status: boolean) {
        this.connectionListeners.forEach((callback) => callback(status));
    }
}
